import { Collapse, Descriptions as AntdDescriptions, Tabs, Tag } from "antd";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ImCheckmark, ImCross } from "react-icons/im";
import { useNavigate } from "react-router-dom";
import AppContext from "../../../context/app/context";
import GeneralSettingContext from "../../../context/general-setting/context";
import GeneralSettingContextProvider from "../../../context/general-setting/provider";
import Descriptions from "../../general/antd/descriptions";
import Spin from "../../general/antd/spin";
import PageDetails from "../../general/details";
import OldImage from "../../form-components/old-image";
import {
    DollarOutlined,
    CarOutlined,
    MobileOutlined,
    GiftOutlined,
    TeamOutlined,
    ThunderboltOutlined,
} from "@ant-design/icons";

interface Props { }

const GeneralSetting: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { direction, screenSize } = useContext(AppContext);
  const { list, actions, loading } = useContext(GeneralSettingContext);
  const navigate = useNavigate();

  useEffect(() => {
    const getGeneralSetting = async () => {
      await actions.getGeneralSetting();
    };
    getGeneralSetting();
  }, [direction]);

  const BooleanValue = ({ value }: { value: boolean | undefined }) => (
    value ? <ImCheckmark color="green" /> : <ImCross color="red" />
  );

  const column = screenSize == "laptopOrDesktop" ? 2 : 1;

  // ========== Tab 1: التسعير والدفع ==========
  const PricingPaymentTab = () => (
    <Collapse
      defaultActiveKey={['currency', 'ePayment', 'loyalty']}
      items={[
        {
          key: 'currency',
          label: t("currency_and_pricing"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("currencySymbol")} span={2}>
                {list?.currencySymbol}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("exchange_price")}>
                {list?.exchangePrice}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("commission")}>
                {list?.commission}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("minimum_limit")}>
                {list?.minimumLimit ?? "--"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("upper_limit")}>
                {list?.upperLimit ?? "--"}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'ePayment',
          label: t("e_payment_settings"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("isEPaymentEnabled")}>
                <BooleanValue value={list?.isEPaymentEnabled} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("isAlbarakaPaymentEnabled")}>
                <BooleanValue value={list?.isAlbarakaPaymentEnabled} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("isAlshamPaymentEnable")}>
                <BooleanValue value={list?.isAlshamPaymentEnable} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("dailyPaymentLimit")}>
                {list?.dailyPaymentLimit}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("weeklyPaymentLimit")}>
                {list?.weeklyPaymentLimit}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("monthlyPaymentLimit")}>
                {list?.monthlyPaymentLimit}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'loyalty',
          label: t("loyalty_and_rewards"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("pointPrice")}>
                {list?.pointPrice}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("pointMultiplicationFactor")}>
                {list?.pointMultiplicationFactor}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("scoreValidity")}>
                {list?.scoreValidity}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("loyaltyProgramLink")}>
                {list?.loyaltyProgramLink}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
      ]}
    />
  );

  // ========== Tab 2: التوصيل والطلبات ==========
  const DeliveryOrdersTab = () => (
    <Collapse
      defaultActiveKey={['deliveryPricing', 'deliveryOptions', 'driver', 'pharmacy', 'orders']}
      items={[
        {
          key: 'deliveryPricing',
          label: t("delivery_pricing"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("kilometerCost")}>
                {list?.kilometerCost}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("priceKilometerCustomer")}>
                {list?.priceKilometerCustomer}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("fixed_delivery_price_on_client")}>
                {list?.fixedDeliveryPriceOnClient}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("priceTotalProductsToGetFreeDelivery")}>
                {list?.priceTotalProductsToGetFreeDelivery ?? "--"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("express_delivery_price")}>
                {list?.expressDeliveryPrice ?? "--"}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'deliveryOptions',
          label: t("delivery_options"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("freeDelivery")}>
                <BooleanValue value={list?.freeDelivery} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("freeDeliveryPharmacyPromotion")}>
                <BooleanValue value={list?.freeDeliveryPharmacyPromotion} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("express_delivery_service")}>
                <BooleanValue value={list?.expressDeliveryService} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("use_trip_based_delivery")}>
                <BooleanValue value={list?.useTripBasedDelivery} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("include_return_trip_in_payment")}>
                <BooleanValue value={list?.includeReturnTripInPayment} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("route_strategy")}>
                <Tag
                  color={
                    list?.routeStrategy === 3 ? "gold" :
                    list?.routeStrategy === 2 ? "purple" : "orange"
                  }
                  icon={<ThunderboltOutlined />}
                >
                  {list?.routeStrategy === 3 ? t("strategy_nearest_neighbor") :
                   list?.routeStrategy === 2 ? t("strategy_hybrid_shortest") :
                   t("strategy_optimized")}
                </Tag>
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("enable_route_comparison")}>
                <BooleanValue value={list?.enableRouteComparison} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("first_order_free")}>
                <BooleanValue value={list?.firstOrderFree} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("fixed_delivery_time_minutes")}>
                {list?.fixedDeliveryTime ?? "--"} {list?.fixedDeliveryTime ? t("minutes") : ""}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("express_delivery_time_minutes")}>
                {list?.expressDeliveryTime ?? "--"} {list?.expressDeliveryTime ? t("minutes") : ""}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("order_statistics_start_date")}>
                {list?.orderStatisticsStartDate ?? "--"}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'driver',
          label: t("driver_settings"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("deliveryWorkerAcceptanceTime")}>
                {list?.deliveryWorkerAcceptanceTime}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("driverTimeBroadcast")}>
                {list?.driverTimeBroadcast}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("maxDriversInOrder")}>
                {list?.maxDriversInOrder}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("radiusSearchForDriver")}>
                {list?.radiusSearchForDriver}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("numberTimesIgnoredAllowed")}>
                {list?.numberTimesIgnoredAllowed}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'pharmacy',
          label: t("pharmacy_settings"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("pharmacyAcceptanceTime")}>
                {list?.pharmacyAcceptanceTime}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("maxNumberPharmaciesFindPharmacie")}>
                {list?.maxNumberPharmaciesFindPharmacie}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("maxNumberPharmaciesFindMedicine")}>
                {list?.maxNumberPharmaciesFindMedicine}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("showListPharmacies")}>
                <BooleanValue value={list?.showListPharmacies} />
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'orders',
          label: t("order_settings"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("client_acceptance_time")}>
                {list?.clientAcceptanceTime}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("timeBroadcast")}>
                {list?.timeBroadcast}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("maxNumOfUnResponseOrder")}>
                {list?.maxNumOfUnResponseOrder}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("delayedOrderSchedulingTime")}>
                {list?.delayedOrderSchedulingTime}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
      ]}
    />
  );

  // ========== Tab 3: التطبيق والتواصل ==========
  const AppCommunicationTab = () => (
    <Collapse
      defaultActiveKey={['publicMessages', 'welcome', 'sharing', 'social', 'general']}
      items={[
        {
          key: 'publicMessages',
          label: t("public_messages"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("publicMessageClient")} span={2}>
                {list?.publicMessageClient}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("publicMessagePharmacist")} span={2}>
                {list?.publicMessagePharmacist}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("publicMessageDlivery")} span={2}>
                {list?.publicMessageDlivery}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("activatePublicMessageClient")}>
                <BooleanValue value={list?.activatePublicMessageClient} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("activatePublicMessagePharmacist")}>
                <BooleanValue value={list?.activatePublicMessagePharmacist} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("activatePublicMessageDelivery")}>
                <BooleanValue value={list?.activatePublicMessageDelivery} />
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'welcome',
          label: t("welcome_settings"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("welcome_message")} span={2}>
                {list?.welcomeMessage}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("welcomeMessageActivation")}>
                <BooleanValue value={list?.welcomeMessageActivation} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("welcomeCoupon")}>
                {list?.welcomeCoupon?.name}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("welcomeCouponActive")}>
                <BooleanValue value={list?.welcomeCouponActive} />
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'sharing',
          label: t("app_sharing"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("sharingAppLink")}>
                {list?.sharingAppLink}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("sharingAppMessage")}>
                {list?.sharingAppMessage ?? "-"}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'social',
          label: t("social_contact"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("facebook")}>
                {list?.facebook ?? "--"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("instagram")}>
                {list?.instagram ?? "--"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("telegram")}>
                {list?.telegram ?? "--"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("whatsApp")}>
                {list?.whatsApp ?? "--"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("phone")}>
                {list?.phone ?? "--"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("hotLine")}>
                {list?.hotLine ?? "--"}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'general',
          label: t("general_options"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("activatingPublicService")}>
                <BooleanValue value={list?.activatingPublicService} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("showMedicalSpecialties")}>
                <BooleanValue value={list?.showMedicalSpecialties} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("showCompanies")}>
                <BooleanValue value={list?.showCompanies} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("otp_service_unavailable")}>
                <BooleanValue value={list?.otpServiceUnavailable} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("circleRadiusMeters")}>
                {list?.circleRadiusMeters}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("new_product_period_days")}>
                {list?.newProductPeriodDays ?? "--"} {list?.newProductPeriodDays ? t("days") : ""}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
      ]}
    />
  );

  // ========== Tab 4: الدعوات ==========
  const InvitationsTab = () => (
    <Collapse
      defaultActiveKey={['invitationEnable', 'senderMessages', 'receiverMessages', 'invitationContent', 'invitationImages']}
      items={[
        {
          key: 'invitationEnable',
          label: t("invitation_activation"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("enable_invitation")}>
                <BooleanValue value={list?.enableInvitation} />
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'senderMessages',
          label: t("sender_messages"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("title_invitation_sender")} span={2}>
                {list?.titleInvitationSender}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("description_invitation_sender")} span={2}>
                {list?.descriptionInvitationSender}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'receiverMessages',
          label: t("receiver_messages"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("title_invitation_receiver")} span={2}>
                {list?.titleInvitationReceiver}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("description_invitation_receiver")} span={2}>
                {list?.descriptionInvitationReceiver}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'invitationContent',
          label: t("invitation_content"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("message_for_sharing_invitation")} span={2}>
                {list?.messageForSharingInvitation}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("small_message_for_invitation")} span={2}>
                {list?.smallMessageForInvitation}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("big_message_for_invitation")} span={2}>
                {list?.bigMessageForInvitation}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("very_small_message_for_invitation")} span={2}>
                {list?.verySmallMessageForInvitation ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: list?.verySmallMessageForInvitation }}
                  />
                ) : (
                  "-"
                )}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'invitationImages',
          label: t("invitation_images"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("image_sender")}>
                <OldImage src={list?.imageIdSender?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("image_receiver")}>
                <OldImage src={list?.imageIdReceiver?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("image_for_big_message")}>
                <OldImage src={list?.imageIdForBigMessage?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("image_for_small_message")}>
                <OldImage src={list?.imageIdForSmallMessage?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("image_id_for_very_small_message")}>
                <OldImage src={list?.imageIdForVerySmallMessage?.url} preview />
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
      ]}
    />
  );

  // ========== Tab 5: المستثمرين والصور ==========
  const InvestorsMediaTab = () => (
    <Collapse
      defaultActiveKey={['investorVisibility', 'headerImages']}
      items={[
        {
          key: 'investorVisibility',
          label: t("investor_visibility"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("makeInvestorsNoResponseOffVisibility")}>
                <BooleanValue value={list?.makeInvestorsNoResponseOffVisibility} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("ordersSummeryInvestorVisibility")}>
                <BooleanValue value={list?.ordersSummeryInvestorVisibility} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("ordersSummeryGuestInvestorVisibility")}>
                <BooleanValue value={list?.ordersSummeryGuestInvestorVisibility} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("startDateTimeOrdersSummeryInvestor")}>
                {list?.startDateTimeOrdersSummeryInvestor?.toLocaleString() ?? "--"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("endDateTimeOrdersSummeryInvestor")}>
                {list?.endDateTimeOrdersSummeryInvestor?.toLocaleString() ?? "--"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("startDateTimeOrdersGuestSummeryInvestor")}>
                {list?.startDateTimeOrdersGuestSummeryInvestor?.toLocaleString() ?? "--"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("endDateTimeOrdersGuestSummeryInvestor")}>
                {list?.endDateTimeOrdersGuestSummeryInvestor?.toLocaleString() ?? "--"}
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: 'headerImages',
          label: t("header_images"),
          children: (
            <Descriptions size="small" column={column} bordered>
              <AntdDescriptions.Item label={t("homeHeaderClient")}>
                <OldImage src={list?.homeHeaderClient?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("homeHeaderClientEn")}>
                <OldImage src={list?.homeHeaderClientEn?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("homeHeaderClientAr")}>
                <OldImage src={list?.homeHeaderClientAr?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("homeHeaderDriver")}>
                <OldImage src={list?.homeHeaderDriver?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("homeHeaderDriverEn")}>
                <OldImage src={list?.homeHeaderDriverEn?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("homeHeaderDriverAr")}>
                <OldImage src={list?.homeHeaderDriverAr?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("homeHeaderInvestor")}>
                <OldImage src={list?.homeHeaderInvestor?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("homeHeaderInvestorEn")}>
                <OldImage src={list?.homeHeaderInvestorEn?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("homeHeaderInvestorAr")}>
                <OldImage src={list?.homeHeaderInvestorAr?.url} preview />
              </AntdDescriptions.Item>
            </Descriptions>
          ),
        },
      ]}
    />
  );

  const tabItems = [
    {
      key: 'pricing',
      label: <span><DollarOutlined /> {t("pricing_and_payment")}</span>,
      children: <PricingPaymentTab />,
    },
    {
      key: 'delivery',
      label: <span><CarOutlined /> {t("delivery_and_orders")}</span>,
      children: <DeliveryOrdersTab />,
    },
    {
      key: 'app',
      label: <span><MobileOutlined /> {t("app_and_communication")}</span>,
      children: <AppCommunicationTab />,
    },
    {
      key: 'invitations',
      label: <span><GiftOutlined /> {t("invitations")}</span>,
      children: <InvitationsTab />,
    },
    {
      key: 'investors',
      label: <span><TeamOutlined /> {t("investors_and_media")}</span>,
      children: <InvestorsMediaTab />,
    },
  ];

  return (
    <>
      <PageDetails
        navigate
        title={t("details")}
        subtitle={t("general_settings")}
        buttonName={`${t("update_general_settings")}`}
        onClick={() => navigate(`/general-settings/update`)}
        loading={loading.includes('details')}
        onRefresh={() => actions.getGeneralSetting()}
      >
        {loading.includes("list") ? (
          <Spin />
        ) : (
          <Tabs
            defaultActiveKey="pricing"
            items={tabItems}
            size="large"
            tabBarStyle={{ marginBottom: 24 }}
          />
        )}
      </PageDetails>
    </>
  );
};

const GeneralSettingDetailesPage = () => {
  return (
    <GeneralSettingContextProvider>
      <GeneralSetting />
    </GeneralSettingContextProvider>
  );
};
export default GeneralSettingDetailesPage;
